import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FormTemplate,
  FormSubmission,
  GetFormTemplatesResponse,
  GetFormSubmissionsResponse
} from "apis/v3/formTemplatesApi";
import { FORM_IDS } from "../../../constants/v3/formConstants";

const AGREEMENT_FORM_IDS = Object.values(FORM_IDS);

interface FormTemplatesState {
  templates: FormTemplate[];
  submissions: FormSubmission[];
}

const initialState: FormTemplatesState = {
  templates: [],
  submissions: []
};

export const FormTemplatesSlice = createSlice({
  name: "formTemplates",
  initialState,
  reducers: {
    setFormTemplates(
      state,
      { payload }: PayloadAction<GetFormTemplatesResponse>
    ) {
      state.templates = payload.data.forms || [];
    },
    setFormSubmissions(
      state,
      { payload }: PayloadAction<GetFormSubmissionsResponse>
    ) {
      state.submissions = payload.data.submissions || [];
    },
    reset: () => initialState
  }
});

export const {
  setFormTemplates,
  setFormSubmissions,
  reset
} = FormTemplatesSlice.actions;

export default FormTemplatesSlice.reducer;

export const selectAllFormTemplates = (state: {
  formTemplates: FormTemplatesState;
}) => state.formTemplates.templates;

export const selectAllFormSubmissions = (state: {
  formTemplates: FormTemplatesState;
}) => state.formTemplates.submissions;

export const selectFormTemplateById = (formId: string) => (state: {
  formTemplates: FormTemplatesState;
}) => state.formTemplates.templates.find((template) => template.id === formId);

export const selectSubmissionByFormId = (formId: string) => (state: {
  formTemplates: FormTemplatesState;
}) => {
  const submissions = state.formTemplates.submissions.filter(
    (submission) => submission.formInfo.id === formId
  );
  if (submissions.length === 0) return null;
  return submissions.reduce((latest, current) =>
    current.submittedOn > latest.submittedOn ? current : latest
  );
};

export const selectHasSubmittedCurrentVersion = (formId: string) => (state: {
  formTemplates: FormTemplatesState;
}) => {
  const template = state.formTemplates.templates.find((t) => t.id === formId);
  if (!template) return false;

  const submission = selectSubmissionByFormId(formId)(state);
  if (!submission) return false;

  return submission.version === template.version;
};

export const selectHasAcceptedCurrentPrivacyPolicy = (state: {
  formTemplates: FormTemplatesState;
}) => selectHasSubmittedCurrentVersion(FORM_IDS.PRIVACY_POLICY)(state);
export const selectHasAcceptedCurrentTermsAndConditions = (state: {
  formTemplates: FormTemplatesState;
}) => selectHasSubmittedCurrentVersion(FORM_IDS.TERMS_AND_CONDITIONS)(state);
export const selectHasAcceptedCurrentHipaaAuthorization = (state: {
  formTemplates: FormTemplatesState;
}) => selectHasSubmittedCurrentVersion(FORM_IDS.HIPAA_AUTHORIZATION)(state);

export const selectUserHasAcceptedAllCurrentAgreements = (state: {
  formTemplates: FormTemplatesState;
}) => {
  if (state.formTemplates.templates.length === 0) {
    return true;
  }

  return (
    selectHasAcceptedCurrentPrivacyPolicy(state) &&
    selectHasAcceptedCurrentTermsAndConditions(state) &&
    selectHasAcceptedCurrentHipaaAuthorization(state)
  );
};

export const selectUserHasNeverAcceptedSomeAgreements = (state: {
  formTemplates: FormTemplatesState;
}) => {
  if (state.formTemplates.templates.length === 0) {
    return false;
  }
  return AGREEMENT_FORM_IDS.some(
    (formId) => selectSubmissionByFormId(formId)(state) === null
  );
};

export const selectUserAgreementData = (state: {
  formTemplates: FormTemplatesState;
}) => {
  const buildAgreementState = (formId: string) => {
    const template = state.formTemplates.templates.find((t) => t.id === formId);
    const submission = selectSubmissionByFormId(formId)(state);

    return {
      acceptedDate: submission
        ? new Date(submission.submittedOn * 1000).toISOString()
        : null,
      acceptedVersion: submission ? submission.version : "0.0.0",
      agreementHtml: template?.content ?? null,
      latestVersion: template?.version || null
    };
  };

  return AGREEMENT_FORM_IDS.reduce(
    (acc, formId) => {
      acc[formId] = buildAgreementState(formId);
      return acc;
    },
    {} as Record<
      string,
      {
        acceptedDate: string | null;
        acceptedVersion: string;
        agreementHtml: string | null;
        latestVersion: string | null;
      }
    >
  );
};
